import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import 'moment/locale/es';
import { appConstants } from '../../_constants/app.constants';

const API = appConstants.API_URL;
const normalize = (s) => (s || '').toString().toLowerCase().trim();

/**
 * Modal de biblioteca personal de equipos. Lista los equipos (nombre + escudo)
 * que el usuario ya ha tenido en otros torneos que administra, ya deduplicados.
 * Al seleccionar uno, llama `onPick(item)` y el padre decide qué crear.
 */
export default function TeamLibraryDialog({
  open,
  onClose,
  onPick,
  existingTeamNames,
  busy = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSearch('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tournamentteams/library`);
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'No se pudo cargar la biblioteca');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const existingSet = useMemo(() => {
    if (!existingTeamNames) return new Set();
    if (existingTeamNames instanceof Set) return existingTeamNames;
    return new Set([...existingTeamNames].map(normalize));
  }, [existingTeamNames]);

  const filtered = useMemo(() => {
    const q = normalize(search);
    if (!q) return items;
    return items.filter((it) => normalize(it.name).includes(q));
  }, [items, search]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: '#0f3531' }}>
        Tu biblioteca de equipos
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.55 }}>
          Equipos que ya has tenido en otros torneos que administras. Al elegir uno se
          crea en este torneo con el mismo nombre y escudo.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar equipo…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2, fontWeight: 600 }}>
            {error}
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {items.length === 0
              ? 'Tu biblioteca está vacía. Cuando crees equipos en torneos que administres, aparecerán aquí.'
              : 'No hay resultados con ese nombre.'}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 420, overflow: 'auto', py: 0 }}>
            {filtered.map((it, i) => {
              const alreadyAdded = existingSet.has(normalize(it.name));
              return (
                <ListItem
                  key={`${it.name}-${i}`}
                  disablePadding
                  sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(15,53,49,0.08)',
                    mb: 0.75,
                    overflow: 'hidden',
                    opacity: alreadyAdded ? 0.6 : 1,
                  }}
                >
                  <ListItemButton
                    disabled={alreadyAdded || busy}
                    onClick={() => onPick && onPick(it)}
                    sx={{ py: 1 }}
                  >
                    <ListItemAvatar>
                      <Avatar src={it.shield || undefined} variant="rounded" sx={{ width: 40, height: 40 }}>
                        {!it.shield ? (it.name || '?').charAt(0) : null}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body1" sx={{ fontWeight: 700 }}>{it.name}</Typography>}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {it.lastTournamentName ? `Visto en «${it.lastTournamentName}» · ` : ''}
                          {moment(it.lastUsedAt).format('D MMM YYYY')}
                        </Typography>
                      }
                    />
                    {alreadyAdded && (
                      <Chip
                        size="small"
                        label="Ya añadido"
                        sx={{
                          ml: 1,
                          bgcolor: 'rgba(201,162,39,0.18)',
                          color: '#5c4a10',
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
